// Browser profile DTOs deliberately contain no OAuth, push or authentication secrets.
const scalarFields = [
  'id', 'firstName', 'lastName', 'fullName', 'email', 'role', 'photoURL', 'locale',
  'status', 'username', 'about', 'gender', 'birthdate', 'website', 'phoneNumber',
  'street', 'streetNumber', 'postalCode', 'city', 'country', 'lastActivity', 'created_at',
  'chatNotifications', 'taskNotifications', 'calendarNotifications', 'newMessageTalk',
  'newsAnnouncement', 'learningNotifications', 'practitionerCode', 'ascaNumber',
  'rmeNumber', 'selfEmployedNumber', 'glnNumber', 'rccNumber', 'iban', 'field',
  'smsCredit', 'smsCreditPurchased', 'height', 'currentWeight', 'goalKcalDay',
  'goalWeight', 'hateFood', 'waterConsumption', 'workTitle', 'hipCircumference',
  'waistCircumference', 'shoppingListNotifications', 'mealPlanNotifications',
  'accountInfoNotifications', 'healthChartNotifications', 'cardNotifications',
]
const pricingFields = ['id', 'title', 'val', 'monthlyPrice', 'yearlyPrice', 'maxPatient', 'minPatient', 'smsCredit']
const topicFields = ['id', 'title', 'val', 'color', 'practitionerid']
const practitionerFields = ['id', 'fullName', 'firstName', 'lastName', 'photoURL', 'email', 'phoneNumber', 'country', 'status']
const commonEditable = ['firstName', 'lastName', 'email', 'status']
const practitionerInformation = [
  'about', 'gender', 'country', 'locale', 'birthdate', 'phoneNumber', 'street',
  'postalCode', 'website', 'streetNumber', 'city', 'field', 'iban', 'ascaNumber',
  'rmeNumber', 'selfEmployedNumber', 'glnNumber', 'rccNumber',
]
const practitionerNotifications = ['chatNotifications', 'taskNotifications', 'calendarNotifications', 'newMessageTalk', 'newsAnnouncement', 'learningNotifications']
const patientNotifications = ['shoppingListNotifications', 'chatNotifications', 'calendarNotifications', 'mealPlanNotifications', 'newsAnnouncement', 'accountInfoNotifications', 'healthChartNotifications', 'cardNotifications']

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)
const isScalar = value => value === null || ['string', 'boolean'].includes(typeof value)
  || (typeof value === 'number' && Number.isFinite(value))
const pickScalars = (input, fields) => Object.fromEntries(fields
  .filter(key => Object.prototype.hasOwnProperty.call(input, key) && isScalar(input[key]))
  .map(key => [key, input[key]]))
const parseProfile = value => {
  if (typeof value === 'string') {
    try { return JSON.parse(value) } catch { return null }
  }
  return value
}

export function sanitizeAccountProfile(value) {
  const profile = parseProfile(value)
  if (!isObject(profile)) return null
  const safe = pickScalars(profile, scalarFields)
  if (typeof profile.demo_mode === 'boolean') safe.demo_mode = profile.demo_mode
  for (const [key, fields] of [['canton', ['id', 'title']], ['pricingplan', pricingFields], ['practitioner', practitionerFields]]) {
    if (profile[key] === null) safe[key] = null
    else if (isObject(profile[key])) safe[key] = pickScalars(profile[key], fields)
  }
  if (Array.isArray(profile.tableTopic)) safe.tableTopic = profile.tableTopic.filter(isObject).map(topic => pickScalars(topic, topicFields))
  if (Array.isArray(profile.ability)) {
    safe.ability = profile.ability.filter(isObject).map(rule => {
      const result = {}
      for (const key of ['action', 'subject']) {
        if (typeof rule[key] === 'string') result[key] = rule[key]
        else if (Array.isArray(rule[key])) result[key] = rule[key].filter(item => typeof item === 'string')
      }
      return result
    }).filter(rule => rule.action && rule.subject)
  }
  // Convert legacy credentials once, without retaining their values in the DTO.
  if ('id' in safe || 'google_connected' in profile || 'googleAccessToken' in profile || 'googleRefreshToken' in profile) {
    safe.google_connected = typeof profile.google_connected === 'boolean'
      ? profile.google_connected : Boolean(profile.googleAccessToken && profile.googleRefreshToken)
    safe.google_calendar_configured = typeof profile.google_calendar_configured === 'boolean'
      ? profile.google_calendar_configured : Boolean(profile.googleCalendarId)
  }
  return safe
}

export function writeStoredAccountProfile(value) {
  const safe = sanitizeAccountProfile(value)
  try {
    if (safe === null) localStorage.removeItem('userData')
    else {
      const serialized = JSON.stringify(safe)
      if (localStorage.getItem('userData') !== serialized) localStorage.setItem('userData', serialized)
    }
    return safe
  } catch {
    // Do not return an unfiltered profile if browser storage is unavailable.
    try { localStorage.removeItem('userData') } catch { /* Storage may be disabled. */ }
    return null
  }
}

export function readStoredAccountProfile() {
  try {
    const stored = localStorage.getItem('userData')
    if (stored === null) return null
    const safe = sanitizeAccountProfile(stored)
    const normalized = safe === null ? null : JSON.stringify(safe)
    if (stored !== normalized) return writeStoredAccountProfile(safe)
    return safe
  } catch {
    try { localStorage.removeItem('userData') } catch { /* Storage may be disabled. */ }
    return null
  }
}

export function buildEditableAccountProfile(value, section = 'all') {
  const profile = parseProfile(value)
  if (!isObject(profile)) return {}
  // Demo identity and billing details are server-owned reference data.
  if (profile.demo_mode === true && ['general', 'information'].includes(section)) return {}
  const isPractitioner = profile.role === 'practitioner'
  const notificationFields = isPractitioner ? practitionerNotifications : patientNotifications
  const fields = section === 'general' ? commonEditable.filter(key => key !== 'status')
    : section === 'information' ? (isPractitioner ? practitionerInformation : [])
      : section === 'notifications' ? notificationFields
        : section === 'status' ? ['status']
          : section === 'chat' ? ['status', 'chatNotifications']
          : [...commonEditable.filter(key => key !== 'status'), ...(isPractitioner ? practitionerInformation : []), ...notificationFields]
  const editable = pickScalars(profile, profile.demo_mode === true && section === 'all' ? notificationFields : fields)
  if (profile.demo_mode !== true && ['all', 'information'].includes(section)) {
    if (profile.canton === null) editable.canton = null
    else if (isObject(profile.canton) && typeof profile.canton.id === 'string') editable.canton = { id: profile.canton.id }
    if (isPractitioner && Array.isArray(profile.tableTopic)) editable.tableTopic = profile.tableTopic
      .filter(topic => isObject(topic) && typeof topic.id === 'string').map(topic => ({ id: topic.id }))
  }
  return editable
}

const requestSequences = new Map()
export function captureProfileRequest(scope = 'profile') {
  const sequence = (requestSequences.get(scope) || 0) + 1
  requestSequences.set(scope, sequence)
  const profile = readStoredAccountProfile()
  return { id: profile?.id || null, token: localStorage.getItem('accessToken'), scope, sequence, profile: JSON.stringify(profile) }
}

const bearerToken = headers => {
  const value = headers?.get?.('Authorization') || headers?.Authorization || headers?.authorization
  return typeof value === 'string' ? value.match(/^Bearer\s+(\S+)$/i)?.[1] : null
}

export function isProfileRequestCurrent(snapshot, response, requireLatest = true) {
  if (!snapshot?.id || !snapshot.token || readStoredAccountProfile()?.id !== snapshot.id) return false
  if (requireLatest && requestSequences.get(snapshot.scope) !== snapshot.sequence) return false
  const currentToken = localStorage.getItem('accessToken')
  if (currentToken === snapshot.token) return true
  // Axios may have accepted a refresh belonging to this exact request before its caller runs.
  return Boolean(currentToken && bearerToken(response?.config?.headers) === snapshot.token
    && bearerToken(response?.headers) === currentToken)
}

export function isLoginRequestCurrent(snapshot) {
  return Boolean(snapshot && requestSequences.get(snapshot.scope) === snapshot.sequence
    && localStorage.getItem('accessToken') === snapshot.token
    && (readStoredAccountProfile()?.id || null) === snapshot.id)
}

export function updateStoredGoogleStatus(snapshot, connected, configured, response) {
  if (!isProfileRequestCurrent(snapshot, response)) return null
  const profile = readStoredAccountProfile()
  if (typeof connected === 'boolean') profile.google_connected = connected
  if (typeof configured === 'boolean') profile.google_calendar_configured = configured
  return writeStoredAccountProfile(profile)
}

export function applyProfileResponse(snapshot, response, replaceChangedProfile = true) {
  if (!isProfileRequestCurrent(snapshot, response) || !response?.data?.success) return null
  const profile = sanitizeAccountProfile(response.data.userData)
  if (!profile || profile.id !== snapshot.id) return null
  // A background read started before a local edit must not undo that edit.
  const currentProfile = readStoredAccountProfile()
  if (!replaceChangedProfile && JSON.stringify(currentProfile) !== snapshot.profile) return currentProfile
  profile.fullName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
  return writeStoredAccountProfile(profile)
}
